import {
  AfterContentInit,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Subscription } from 'rxjs';

import { DOMService } from '~/core/services/dom/dom.service';
import { KeyboardEnum } from '~/component/enums/keyboard.enum';
import { MatchMediaService } from '~/core/services/match-media/match-media.service';
import { VtmnPlayButtonVariantEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-variant.enum';
import { VtmnPlayButtonShapeEnum } from '../buttons/vtmn-play-button/enum/vtmn-play-button-shape.enum';
import { slideInOut } from '~/core/helpers/animation/animation.helper';
import { CtaButtonTypeEnum } from '../call-to-action/enums/button-type.enum';
import { VtmnPlayIconButtonVariantEnum } from '../buttons/vtmn-play-icon-button/enum/vtmn-play-icon-button-variant.enum';
import { VtmnPlayIconTypeEnum } from '../vtmn-play-icon/enums/vtmn-play-icon-type.enum';

@Component({
  selector: 'app-panel',
  templateUrl: './panel.component.html',
  styleUrls: ['./panel.component.scss'],
  animations: [slideInOut()],
})
export class PanelComponent
  implements AfterContentInit, OnDestroy, OnInit, OnChanges
{
  @ContentChild('footerContent') public footerContent: ElementRef;
  @ContentChild('headerContent') public headerContent: ElementRef;
  @Input() public isCart = false;
  @Input() public isOpen = false;
  @Input() public panelTitle = false;
  @Output() public readonly panelClose = new EventEmitter<boolean>();
  @ViewChild('closeButton', { read: ElementRef })
  public closeButton: ElementRef;

  private readonly subscriptions = new Subscription();

  public readonly iconType = VtmnPlayIconTypeEnum;
  public readonly ctaButtonType = CtaButtonTypeEnum;
  public readonly vtmnPlayButtonShape = VtmnPlayButtonShapeEnum;
  public readonly vtmnPlayButtonVariant = VtmnPlayButtonVariantEnum;
  public readonly vtmnPlayIconButtonVariant = VtmnPlayIconButtonVariantEnum;

  public hasFooter = false;
  public hasHeaderContent = false;
  public isMobileDevice: boolean;

  constructor(
    private readonly domService: DOMService,
    private readonly elementRef: ElementRef,
    private readonly matchMediaService: MatchMediaService
  ) {}

  public ngAfterContentInit(): void {
    this.hasHeaderContent = Boolean(this.headerContent?.nativeElement);
    this.hasFooter = Boolean(this.footerContent?.nativeElement);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (!changes?.isOpen) {
      return;
    }

    if (changes?.isOpen?.currentValue) {
      this.domService.disableBodyScroll();
    } else {
      this.requestClose();
    }
  }

  public ngOnDestroy(): void {
    this.domService.enableBodyScroll();
  }

  public ngOnInit(): void {
    this.subscriptions.add(
      this.matchMediaService.isMobileDevice().subscribe((isMobileDevice) => {
        this.isMobileDevice = isMobileDevice;
      })
    );
  }

  public get focusableElements() {
    return [
      ...this.elementRef.nativeElement.querySelectorAll(
        'a, button:not(.loop-focus), input, select, textarea'
      ),
    ].filter(
      (node) => node.disabled !== true && node.getAttribute('tabindex') !== '-1'
    );
  }

  public get openTrigger(): string {
    return this.isMobileDevice ? 'slideFromBottom' : 'slideFromRight';
  }

  @HostListener('document:keydown', ['$event'])
  public handleKeyboardEvent(event: KeyboardEvent): void {
    if (event.code === KeyboardEnum.Escape) {
      this.requestClose();
    }
  }

  public focusFirstFocusable(): void {
    this.focusableElements[0]?.focus();
  }

  public focusLastFocusable(): void {
    this.focusableElements?.pop()?.focus();
  }

  public onAnimationEvent(_event: AnimationEvent): void {
    if (this.isOpen) {
      this.closeButton?.nativeElement.focus();
    }
  }

  public requestClose(): void {
    this.domService.enableBodyScroll();
    this.panelClose.emit(true);
  }
}
